"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Stethoscope, FlaskConical, Sparkles, HeartHandshake } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Diagnóstico médico preciso",
  description: "Evaluamos cada caso de forma individualizada mediante tecnología avanzada para identificar la causa exacta de la alopecia y definir el mejor plan de acción.",
  icon: Stethoscope
}, {
  title: "Tratamientos personalizados",
  description: "Diseñamos protocolos a medida combinando las terapias médicas y quirúrgicas más efectivas según las necesidades específicas de tu cabello.",
  icon: FlaskConical
}, {
  title: "Resultados naturales",
  description: "Nuestro objetivo principal es restaurar tu densidad capilar manteniendo una apariencia completamente natural, armónica e indetectable.",
  icon: Sparkles
}, {
  title: "Acompañamiento continuo",
  description: "Realizamos un seguimiento médico exhaustivo de tu evolución, ajustando el tratamiento en cada fase para garantizar el éxito a largo plazo.",
  icon: HeartHandshake
}];
function AnimatedFeatureCard({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full rounded-sm border-none shadow-xl bg-card text-card-foreground hover:-translate-y-1 transition-transform duration-300">
        <CardHeader className="pb-4">
          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <feature.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-xl font-default leading-tight">
            {feature.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
            {feature.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function WhyChooseUs() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="whychooseus" className="bg-background">
      <div className="bg-secondary text-secondary-foreground py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div ref={headerRef} className={`flex flex-col items-center text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="text-primary tracking-widest uppercase text-sm block mb-6 !font-bold">
              Excelencia Médica
            </span>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold mb-8 max-w-3xl">
              ¿Por qué elegir MS Instituto Capilar?
            </h2>
            
            {}
            <div className="w-1 h-16 bg-primary rounded-sm mb-8 opacity-80"></div>
            
            <p className="text-secondary-foreground/80 max-w-2xl text-lg md:text-xl font-light">
              Más que tratamientos capilares, ofrecemos atención médica personalizada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => <AnimatedFeatureCard key={index} feature={feature} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}